// Moved from `src/hooks/query/useQueryEntitlements.ts` (now a shim). Two
// app-shaped inputs arrive through the injected config seam (design D3):
// the reactive auth token (was a Rematch `useSelector` — injected as a HOOK
// so token refresh still re-keys the query) and — since the SCC inversion
// (ARCFE-986) — ONE domain-neutral awaited port, `onEntitlementsResolved`,
// which replaced the five-callback coding `sccAccess` injection. Query
// behaviour is byte-identical; the SCC orchestration moved verbatim into
// `@heidi/coding/data`'s `ensureSccAccessContext`.

import { skipToken, useQuery } from '@tanstack/react-query';
import { GET_ENTITLEMENTS, type UserEntitlementView } from '../../contracts';
import { getEntitlements } from '../api/entitlements';
import { getWorkspaceDataConfig } from '../config';
import { useQueryUser } from './useQueryUser';

export { GET_ENTITLEMENTS };

/**
 * Hook to fetch access entitlements for the current user.
 * When coder_seat is enabled, also mints/caches the SCC coding JWT via
 * POST /coding-cases/access-context (RCM-851).
 */
export function useQueryEntitlements(entitlementNames: string[] = []) {
  const token = getWorkspaceDataConfig().useAuthToken();
  const { data: user_ref_id } = useQueryUser((data) => data.user_ref_id);

  return useQuery({
    // Include token so Heidi refresh remints the SCC JWT.
    queryKey: [GET_ENTITLEMENTS, user_ref_id, token],
    queryFn:
      !!token && !!user_ref_id
        ? async () => {
            const { data } = await getEntitlements();
            const entitlementMap = new Map(data.entitlements.map((e) => [e.name, e]));

            // MR-WORKSPACE-SCC-INVERSION (ARCFE-986): the SCC mint that ran
            // inline here through five injected coding callbacks is now an
            // app-wired reaction. AWAITED so the legacy ordering holds — the
            // reaction settles before the query resolves. The port contract
            // forbids rejection (the legacy block swallowed mint failures),
            // so no try/catch here: adding one would hide a reaction that
            // breaks its own contract.
            await getWorkspaceDataConfig().onEntitlementsResolved?.(entitlementMap);

            return entitlementMap;
          }
        : skipToken,

    select: (fullMap) => {
      // 1. If no names provided, return the full Map
      if (entitlementNames.length === 0) return fullMap;

      // 2. Create a new filtered Map
      const filteredMap = new Map<string, UserEntitlementView>();

      for (const name of entitlementNames) {
        const entry = fullMap.get(name);
        if (entry) {
          filteredMap.set(name, entry);
        }
      }

      return filteredMap;
    },
  });
}
